import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Store as StoreIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import apiService from '../../services/api';
import DireccionForm from '../../components/forms/DireccionForm';
import HorarioEditor from '../../components/forms/HorarioEditor';
import { 
  ComercioExtendido, 
  Local, 
  LocalInput, 
  Direccion, 
  Horario,
  HORARIO_DEFAULT 
} from '../../types/comercio';

const ComercioLocales: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const comercioId = parseInt(id || '0');

  // Estados principales
  const [comercio, setComercio] = useState<ComercioExtendido | null>(null);
  const [locales, setLocales] = useState<Local[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Estados del diálogo
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocal, setEditingLocal] = useState<Local | null>(null);
  const [localForm, setLocalForm] = useState<LocalInput>({
    nombre: '',
    direccion: {
      calle: '',
      numero: '',
      departamento_id: '',
      provincia_id: '',
      distrito_id: '',
      referencia: ''
    },
    hereda_horario: true,
    horario_propio: HORARIO_DEFAULT
  });

  useEffect(() => {
    if (comercioId) {
      loadComercio();
    }
  }, [comercioId]);

  const loadComercio = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiService.getComercio(comercioId);
      if (response.success) {
        setComercio(response.data);
        setLocales(response.data.locales || []);
      } else {
        setError(response.message || 'Error cargando comercio');
      }
    } catch (error) {
      console.error('Error cargando comercio:', error);
      setError('Error cargando comercio');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (local?: Local) => {
    if (local) {
      setEditingLocal(local);
      setLocalForm({
        nombre: local.nombre,
        direccion: local.direccion,
        hereda_horario: local.hereda_horario,
        horario_propio: local.horario_propio || HORARIO_DEFAULT
      });
    } else {
      setEditingLocal(null);
      setLocalForm({
        nombre: '',
        direccion: {
          calle: '',
          numero: '',
          departamento_id: comercio?.direccion_principal?.departamento_id || '',
          provincia_id: comercio?.direccion_principal?.provincia_id || '',
          distrito_id: comercio?.direccion_principal?.distrito_id || '',
          referencia: ''
        },
        hereda_horario: true,
        horario_propio: HORARIO_DEFAULT
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLocal(null);
  };

  const handleSaveLocal = async () => {
    setLoading(true);
    
    try {
      let response;
      if (editingLocal) {
        response = await apiService.updateLocal(comercioId, editingLocal.id, localForm);
      } else {
        response = await apiService.createLocal(comercioId, localForm);
      }

      if (response.success) {
        handleCloseDialog();
        loadComercio(); // Recargar datos
      } else {
        setError(response.message || 'Error guardando local');
      }
    } catch (error) {
      console.error('Error guardando local:', error);
      setError('Error guardando local');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocal = async (localId: number) => {
    if (window.confirm('¿Está seguro de eliminar este local?')) {
      try {
        const response = await apiService.deleteLocal(comercioId, localId);
        if (response.success) {
          loadComercio(); // Recargar datos
        } else {
          setError(response.message || 'Error eliminando local');
        }
      } catch (error) {
        setError('Error eliminando local');
      }
    }
  };

  const formatearDireccion = (direccion: Direccion): string => {
    if (!direccion || !direccion.calle) return 'No registrada';
    return `${direccion.calle}${direccion.numero ? ` ${direccion.numero}` : ''}${direccion.distrito_nombre ? `, ${direccion.distrito_nombre}` : ''}`;
  };

  const formatearHorario = (local: Local): string => {
    if (local.hereda_horario) {
      return 'Hereda del negocio';
    }
    // Mostrar resumen básico del horario
    const horario = local.horario_propio;
    if (!horario) return 'No definido';
    
    const diasAbiertos = Object.entries(horario).filter(([_, dia]) => dia.abierto).length;
    return `${diasAbiertos}/7 días activos`;
  };

  if (loading && !comercio) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!comercio) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Comercio no encontrado</Alert>
      </Box>
    );
  }

  return (
    <Layout>
      <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin/comercios-ext')}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1">
            Locales de {comercio.razon_social}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            RUC: {comercio.ruc}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Agregar Local
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Información del negocio principal */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <StoreIcon sx={{ mr: 1 }} />
          Negocio Principal
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">Dirección:</Typography>
            <Typography variant="body1">{formatearDireccion(comercio.direccion_principal)}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">Horario base:</Typography>
            <Typography variant="body1">
              <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              Ver horario detallado
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tabla de locales */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Locales Adicionales ({locales.length})
          </Typography>
        </Box>
        
        {locales.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <StoreIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No hay locales adicionales
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Agrega locales para expandir la presencia de este comercio
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Agregar Primer Local
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Dirección</TableCell>
                  <TableCell>Horario</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locales.map((local) => (
                  <TableRow key={local.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {local.nombre}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 250 }}>
                        {formatearDireccion(local.direccion)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatearHorario(local)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={local.activo ? 'Activo' : 'Inactivo'}
                        color={local.activo ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(local)}
                          title="Editar"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteLocal(local.id)}
                          color="error"
                          title="Eliminar"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog para agregar/editar local */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLocal ? 'Editar Local' : 'Nuevo Local'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Nombre del local */}
            <TextField
              fullWidth
              label="Nombre del Local *"
              value={localForm.nombre}
              onChange={(e) => setLocalForm({ ...localForm, nombre: e.target.value })}
              placeholder="Ej: Sucursal San Isidro, Local Centro, etc."
            />

            <Divider />

            {/* Dirección */}
            <Box>
              <Typography variant="h6" gutterBottom>Dirección</Typography>
              <DireccionForm
                value={localForm.direccion}
                onChange={(direccion) => setLocalForm({ ...localForm, direccion })}
              />
            </Box>

            <Divider />

            {/* Horario */}
            <Box>
              <Typography variant="h6" gutterBottom>Horario de Atención</Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={localForm.hereda_horario}
                    onChange={(e) => setLocalForm({ 
                      ...localForm, 
                      hereda_horario: e.target.checked 
                    })}
                  />
                }
                label="Heredar horario del negocio principal"
                sx={{ mb: 2 }}
              />

              {!localForm.hereda_horario && (
                <HorarioEditor
                  value={localForm.horario_propio || HORARIO_DEFAULT}
                  onChange={(horario) => setLocalForm({ 
                    ...localForm, 
                    horario_propio: horario 
                  })}
                  title="Horario Personalizado"
                  subtitle="Define un horario específico para este local"
                />
              )}

              {localForm.hereda_horario && (
                <Alert severity="info">
                  Este local usará el mismo horario configurado para el negocio principal.
                  Puedes desactivar esta opción para configurar un horario personalizado.
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveLocal}
            disabled={!localForm.nombre || !localForm.direccion.calle}
          >
            {editingLocal ? 'Actualizar' : 'Crear'} Local
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Layout>
  );
};

export default ComercioLocales;
